import { Skeleton } from '@mui/material';
import {
    IconGridDots,
    IconHome,
    IconPencil,
    IconRefresh,
    IconRosetteDiscountCheckFilled,
    IconSend,
    IconShare,
    IconUserOff,
    IconAlertCircle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@fragments';
import { UserAvatar } from '@components/Community';
import PostModal from '@modals/Group/PostModal';

import CommunitiesCarousel from './components/CommunitiesCarousel';
import PostsGrid from './components/PostsGrid';
import ShareProfileModal from './components/ShareProfileModal';

import './styles.scss';

/**
 * ProfileView — the public profile in the Instagram desktop grammar: a large
 * avatar beside the identity block (name + action, handle, counts, bio), a
 * horizontal rail of small community cards where the story circles would be,
 * then a hairline tab strip over the 3-column posts grid.
 */
export default function ProfileView(props) {
    const { t } = useTranslation('member');

    const {
        status,
        isOwn,
        user,
        description,
        postsTotal,
        communities,
        posts,
        activePost,
        profileUrl,
        shareOpen,
        onRetry,
        onGoHome,
        onEditProfile,
        onSendMessage,
        onOpenCommunity,
        onOpenPost,
        onClosePost,
        onOpenShare,
        onCloseShare,
    } = props;

    if (status === 'loading') return <LoadingLayout />;

    if (status === 'notFound') {
        return (
            <StateLayout
                icon={<IconUserOff />}
                title={t('publicProfile.notFound.title')}
                subtitle={t('publicProfile.notFound.subtitle')}
                action={(
                    <Button color="inverse" size="small" startIcon={<IconHome />} onClick={onGoHome}>
                        {t('publicProfile.notFound.cta')}
                    </Button>
                )}
            />
        );
    }

    if (status === 'error' || !user) {
        return (
            <StateLayout
                icon={<IconAlertCircle />}
                title={t('publicProfile.error.title')}
                subtitle={t('publicProfile.error.subtitle')}
                action={(
                    <Button color="inverse" size="small" startIcon={<IconRefresh />} onClick={onRetry}>
                        {t('publicProfile.error.cta')}
                    </Button>
                )}
            />
        );
    }

    return (
        <div className="profile">
            {/* ── Identity ─────────────────────────────────────────────── */}
            <header className="profile__header profile__rise">
                <div className="profile__avatar">
                    <UserAvatar user={user} size={150} link={false} />
                </div>

                <div className="profile__identity">
                    <Text.h2
                        weight={500}
                        contrast={10}
                        maxLines={1}
                        className="profile__name"
                        right={user.verified ? <IconRosetteDiscountCheckFilled className="profile__verified" /> : undefined}
                    >
                        {user.username}
                    </Text.h2>

                    {user.nickname && (
                        <Text.span contrast={6} className="profile__handle">@{user.nickname}</Text.span>
                    )}

                    <ul className="profile__stats">
                        <li className="profile__stat">
                            <Text.label contrast={10}>{postsTotal}</Text.label>
                            <Text.span contrast={6}>{t('publicProfile.stats.posts', { count: postsTotal })}</Text.span>
                        </li>
                        <li className="profile__stat">
                            <Text.label contrast={10}>{communities.length}</Text.label>
                            <Text.span contrast={6}>{t('publicProfile.stats.communities', { count: communities.length })}</Text.span>
                        </li>
                    </ul>

                    {description && (
                        <Text.p contrast={8} className="profile__bio">{description}</Text.p>
                    )}

                    <div className="profile__actions">
                        {isOwn ? (
                            <Button color="overlay" size="small" startIcon={<IconPencil />} onClick={onEditProfile}>
                                {t('publicProfile.editProfile')}
                            </Button>
                        ) : (
                            <Button color="inverse" size="small" startIcon={<IconSend />} onClick={onSendMessage}>
                                {t('publicProfile.sendMessage')}
                            </Button>
                        )}
                        <Button color="overlay" size="small" startIcon={<IconShare />} onClick={onOpenShare} preventLoading>
                            {t('publicProfile.share.button')}
                        </Button>
                    </div>
                </div>
            </header>

            {/* ── Communities rail (where the story circles sit) ────────── */}
            {communities.length > 0 && (
                <section className="profile__section profile__rise" style={{ animationDelay: '60ms' }}>
                    <CommunitiesCarousel communities={communities} onOpen={onOpenCommunity} />
                </section>
            )}

            {/* ── Posts ─────────────────────────────────────────────────── */}
            <section className="profile__section profile__posts profile__rise" style={{ animationDelay: '120ms' }}>
                {/* Only one tab exists today; the strip keeps the reference's
                    silhouette and a place for future tabs. */}
                <div className="profile__tabs" role="tablist">
                    <button type="button" role="tab" aria-selected="true" className="profile__tab profile__tab--active">
                        <Text.subheading contrast={10} left={<IconGridDots />}>
                            {t('publicProfile.posts.tab')}
                        </Text.subheading>
                    </button>
                </div>

                <PostsGrid
                    posts={posts}
                    isOwn={isOwn}
                    ownerName={user.username}
                    onOpen={onOpenPost}
                />
            </section>

            <PostModal postId={activePost?.id} open={!!activePost} onClose={onClosePost} />
            <ShareProfileModal open={shareOpen} user={user} url={profileUrl} onClose={onCloseShare} />
        </div>
    );
}

function StateLayout({ icon, title, subtitle, action }) {
    return (
        <div className="profile">
            <div className="profile__state profile__rise">
                <span className="profile__state-icon">{icon}</span>
                <Text.h5 weight={500} contrast={10}>{title}</Text.h5>
                <Text.span contrast={6}>{subtitle}</Text.span>
                <span className="profile__state-action">{action}</span>
            </div>
        </div>
    );
}

// Mirrors the loaded silhouette so the page doesn't jump when data lands.
function LoadingLayout() {
    return (
        <div className="profile" aria-hidden="true">
            <header className="profile__header">
                <div className="profile__avatar">
                    <Skeleton variant="circular" width={150} height={150} animation="wave" className="profile-skeleton" />
                </div>
                <div className="profile__identity">
                    <Skeleton variant="text" width="40%" height={32} animation="wave" className="profile-skeleton" />
                    <Skeleton variant="text" width="22%" height={16} animation="wave" className="profile-skeleton" />
                    <Skeleton variant="text" width="34%" height={18} animation="wave" className="profile-skeleton" />
                    <Skeleton variant="rounded" width={140} height={34} animation="wave" className="profile-skeleton" />
                </div>
            </header>

            <section className="profile__section">
                <Skeleton variant="text" width={96} height={14} animation="wave" className="profile-skeleton" />
                <div className="profile__skeleton-row">
                    {[0, 1, 2].map(i => (
                        <Skeleton key={i} variant="rounded" width={264} height={60} animation="wave" className="profile-skeleton profile-skeleton--card" />
                    ))}
                </div>
            </section>

            <section className="profile__section profile__posts">
                <div className="profile__tabs">
                    <span className="profile__tab">
                        <Skeleton variant="text" width={96} height={14} animation="wave" className="profile-skeleton" />
                    </span>
                </div>
                <div className="posts-grid">
                    {[0, 1, 2].map(c => (
                        <div className="posts-grid__column" key={c}>
                            <Skeleton variant="rounded" animation="wave" className="profile-skeleton profile-skeleton--tile" />
                            <Skeleton variant="rounded" animation="wave" className="profile-skeleton profile-skeleton--tile" />
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
